import React, { useState, useEffect } from 'react';
import {
  Box,
  Text,
  Button,
  Flex,
  useColorModeValue,
  Icon,
  Link,
  Collapse,
  VStack,
  Switch,
  Heading,
  useBreakpointValue
} from '@chakra-ui/react';
import { FaCookieBite, FaChevronUp, FaChevronDown } from 'react-icons/fa';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const isPrerenderCrawler = () => (
  typeof navigator !== 'undefined' &&
  (navigator.webdriver || /HeadlessChrome|Prerender/i.test(navigator.userAgent || ''))
);

const CookieConsentBanner = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Always true and disabled
    analytics: true,
    marketing: false,
    preferences: true
  });

  // Check if we're on the home page
  const isHomePage = location.pathname === '/';

  // Check if user has already set cookie preferences
  useEffect(() => {
    if (isPrerenderCrawler()) {
      return undefined;
    }

    const hasConsent = localStorage.getItem('cookieConsent');
    if (!hasConsent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Colors based on the theme
  const bgColor = useColorModeValue('rgba(255, 255, 255, 0.98)', 'rgba(26, 32, 44, 0.98)');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const headingColor = useColorModeValue('secondary.500', 'white');
  const iconColor = useColorModeValue('brand.500', 'brand.300');
  const dividerColor = useColorModeValue('gray.200', 'gray.700');

  // Handle accepting all cookies
  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', 'all');
    localStorage.setItem('cookiePreferences', JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    }));
    setIsVisible(false);
  };

  // Handle saving custom preferences
  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', 'custom');
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    setIsVisible(false);
  };

  // Handle preference toggle changes
  const handleToggleChange = (preference) => {
    setCookiePreferences({
      ...cookiePreferences,
      [preference]: !cookiePreferences[preference]
    });
  };

  // Handle declining non-essential cookies
  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'necessary');
    localStorage.setItem('cookiePreferences', JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    }));
    setIsVisible(false);
  };

  // Responsive sizing
  const buttonSize = 'md';
  const containerPadding = useBreakpointValue({ base: 4, md: 5 }, { fallback: 'base' });
  const flexDirection = useBreakpointValue({ base: 'column', md: 'row' }, { fallback: 'base' });
  const textSize = useBreakpointValue({ base: 'sm', md: 'md' }, { fallback: 'base' });
  const headingSize = useBreakpointValue({ base: 'sm', md: 'md' }, { fallback: 'base' });
  const iconSize = useBreakpointValue({ base: '1rem', md: '1.2rem' }, { fallback: 'base' });

  if (!isVisible) {
    return null;
  }

  return (
    <Box
      position="fixed"
      bottom={{
        base: isHomePage ? "80px" : "20px", // Extra space on home page for mobile
        md: "30px"
      }}
      left="50%"
      transform={isVisible ? 'translateX(-50%)' : 'translateX(-50%) translateY(100%)'}
      width={{ base: "95%", sm: "90%", md: "80%", lg: "70%" }}
      maxWidth="900px"
      zIndex={1003} // Higher z-index to ensure it's above running banner
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="xl"
      border="1px solid"
      borderColor={borderColor}
      boxShadow="lg"
      transition="all 0.3s ease"
      opacity={isVisible ? 1 : 0}
      maxHeight={{ base: "70vh", md: "none" }} // Reduced max height on mobile
      overflowY={{ base: "auto", md: "visible" }}
      // Add safe area padding for mobile devices
      pb={{
        base: isHomePage ? "env(safe-area-inset-bottom, 0px)" : "0px",
        md: "0px"
      }}
      // Ensure banner stays within viewport bounds
      sx={{
        '@supports (height: 100dvh)': {
          // Use dynamic viewport height on supported browsers
          maxHeight: { base: '70dvh', md: 'none' }
        }
      }}
    >
      <Box py={containerPadding} px={{ base: 4, md: 5 }}>
        <Flex direction="column" width="100%">
          {/* Main banner content */}
          <Flex 
            align={{ base: 'flex-start', md: 'center' }} 
            justify="space-between" 
            direction={flexDirection}
            gap={4}
          >
            <Flex align="flex-start" gap={3} flex="1">
              <Icon 
                as={FaCookieBite} 
                color={iconColor} 
                fontSize={iconSize}
                mt="0.2rem"
                flexShrink={0}
              />
              <Box>
                <Heading 
                  as="h3" 
                  fontSize={headingSize} 
                  fontWeight="600" 
                  mb={2}
                  color={headingColor}
                >
                  {t('We value your privacy')}
                </Heading>
                <Text 
                  color={textColor} 
                  fontSize={textSize}
                  lineHeight="1.5"
                  mb={{ base: 3, md: 0 }}
                >
                  {t('We use cookies to enhance your browsing experience, personalize content and analyze site traffic. By clicking "Accept All", you consent to our use of cookies.')}
                </Text>
              </Box>
            </Flex>
            
            <Flex
              direction="row"
              gap={3}
              alignSelf={{ base: 'stretch', md: 'flex-start' }}
              w={{ base: 'full', md: 'auto' }}
            >
              <Button
                variant="outline"
                size={buttonSize}
                onClick={() => setShowPreferences(!showPreferences)}
                rightIcon={showPreferences ? <FaChevronUp /> : <FaChevronDown />}
                flex={{ base: 1, sm: '0 0 auto' }}
                minW={{ sm: '140px' }}
              >
                {t('Customize')}
              </Button>
              <Button
                variant="primary"
                size={buttonSize}
                onClick={handleAcceptAll}
                flex={{ base: 1, sm: '0 0 auto' }}
                minW={{ sm: '140px' }}
              >
                {t('Accept All')}
              </Button>
            </Flex>
          </Flex>
          
          {/* Expandable preferences section */}
          <Collapse in={showPreferences} animateOpacity>
            <Box mt={4} pt={4} borderTop="1px solid" borderColor={dividerColor}>
              <VStack spacing={4} align="stretch">
                <Text fontSize={textSize} fontWeight="500" color={textColor}>
                  {t('Manage Cookie Preferences')}
                </Text>
                
                {/* Necessary cookies */}
                <Flex justify="space-between" align="center">
                  <Box>
                    <Text fontWeight="600" fontSize={textSize} color={headingColor}>
                      {t('Necessary Cookies')}
                    </Text>
                    <Text fontSize={textSize === 'md' ? 'sm' : 'xs'} color={textColor}>
                      {t('These cookies are required for the website to function and cannot be disabled.')}
                    </Text>
                  </Box>
                  <Switch 
                    isChecked={cookiePreferences.necessary} 
                    isDisabled={true}
                    colorScheme="brand"
                  />
                </Flex>
                
                {/* Analytics cookies */}
                <Flex justify="space-between" align="center">
                  <Box>
                    <Text fontWeight="600" fontSize={textSize} color={headingColor}>
                      {t('Analytics Cookies')}
                    </Text>
                    <Text fontSize={textSize === 'md' ? 'sm' : 'xs'} color={textColor}>
                      {t('These cookies help us understand how visitors interact with our website.')}
                    </Text>
                  </Box>
                  <Switch 
                    isChecked={cookiePreferences.analytics} 
                    onChange={() => handleToggleChange('analytics')}
                    colorScheme="brand"
                  />
                </Flex>
                
                {/* Marketing cookies */}
                <Flex justify="space-between" align="center">
                  <Box>
                    <Text fontWeight="600" fontSize={textSize} color={headingColor}>
                      {t('Marketing Cookies')}
                    </Text>
                    <Text fontSize={textSize === 'md' ? 'sm' : 'xs'} color={textColor}>
                      {t('These cookies are used to track visitors across websites to display relevant advertisements.')}
                    </Text>
                  </Box>
                  <Switch 
                    isChecked={cookiePreferences.marketing} 
                    onChange={() => handleToggleChange('marketing')}
                    colorScheme="brand"
                  />
                </Flex>
                
                {/* Preferences cookies */}
                <Flex justify="space-between" align="center">
                  <Box>
                    <Text fontWeight="600" fontSize={textSize} color={headingColor}>
                      {t('Preferences Cookies')}
                    </Text>
                    <Text fontSize={textSize === 'md' ? 'sm' : 'xs'} color={textColor}>
                      {t('These cookies allow the website to remember choices you make and provide enhanced functionality.')}
                    </Text>
                  </Box>
                  <Switch 
                    isChecked={cookiePreferences.preferences} 
                    onChange={() => handleToggleChange('preferences')}
                    colorScheme="brand"
                  />
                </Flex>
                
                <Flex 
                  direction={{ base: 'column', md: 'row' }}
                  justify="space-between" 
                  align={{ base: 'stretch', md: 'center' }}
                  mt={2} 
                  pt={2} 
                  borderTop="1px solid" 
                  borderColor={dividerColor}
                  gap={3}
                >
                  <Flex 
                    direction={{ base: 'column', sm: 'row' }}
                    align={{ base: 'stretch', sm: 'center' }}
                    gap={3}
                  >
                    <Button
                      variant="ghost"
                      size={buttonSize}
                      onClick={handleDecline}
                      w={{ base: 'full', sm: 'auto' }}
                    >
                      {t('Decline All')}
                    </Button>
                    <Link
                      as={RouterLink}
                      to="/privacy"
                      color={iconColor}
                      fontSize={textSize}
                      fontWeight="500"
                      textAlign={{ base: 'center', sm: 'left' }}
                      _hover={{
                        textDecoration: 'none',
                        opacity: 0.8
                      }}
                    >
                      {t('Privacy Policy')}
                    </Link>
                  </Flex>
                  <Button
                    variant="primary"
                    size={buttonSize}
                    onClick={handleSavePreferences}
                    w={{ base: 'full', md: 'auto' }}
                    minW={{ base: 'auto', md: '140px' }}
                  >
                    {t('Save Preferences')}
                  </Button>
                </Flex>
              </VStack>
            </Box>
          </Collapse>
        </Flex>
      </Box>
    </Box>
  );
};

export default CookieConsentBanner;
